/**
 * Универсальная форма накладной (приход или расход) — UI 1:1 с Excel-системой:
 * - Шапка документа: контрагент, сотрудник, дата, основание, доп.поля для расхода.
 * - Таблица позиций с inline-добавлением и редактированием количеств/цен.
 * - Кнопки: Данные / Добавить позицию / Сохранить / Очистить / Печать / История.
 *
 * Состояние формы хранится в localStorage чтобы не потерять при F5.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { useQuery } from "@tanstack/react-query";

import { Icon } from "@/components/prototype/Icon";
import { ItemAutocomplete } from "@/components/warehouse/ItemAutocomplete";
import { useAuth } from "@/auth/AuthContext";
import {
  saveCartAsDoc, fetchUnits, fetchDocCounters, fetchObjects, formatObjectLabel,
  formatRub, type ItemRow, type ObjectForSelect,
} from "@/api/warehouse";
import { ApiError } from "@/api/client";
import { usePermissions } from "@/lib/usePermissions";


type Direction = "inflow" | "outflow";


interface FormHeader {
  warehouse: "СКЛАД 2_4" | "ИНСТРУМЕНТ 1.0" | "ОФИС";
  docNumber: string;
  docDate: string;          // YYYY-MM-DD
  contractor: string;
  employee: string;
  basis: string;            // основание (документ-основание)
  // Только для расхода:
  address: string;
  phone: string;
  shipDate: string;
  requestType: string;      // Продажа / Доставка / Самовывоз
  payment: string;
  // Привязка к объекту (только для отгрузок «На Объект»):
  // object_id = production_objects.id, deal_amo_id — дублируем для удобства
  // отчётов (один JOIN не нужен).
  objectId: number | null;
  dealAmoId: number | null;
}

interface FormLine {
  key: string;              // uid для React
  item_id: number | null;
  sku: string | null;
  name: string;
  unit: string | null;
  quantity: number;
  price_buy: number;        // используется для inflow
  price_sell: number;       // для outflow
  on_stock?: number;        // справочно (для outflow)
}


function todayIso(): string {
  return new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function blankHeader(): FormHeader {
  return {
    warehouse: "СКЛАД 2_4",
    docNumber: "",
    docDate: todayIso(),
    contractor: "",
    employee: "",
    basis: "",
    address: "",
    phone: "",
    shipDate: todayIso(),
    requestType: "Продажа",
    payment: "",
    objectId: null,
    dealAmoId: null,
  };
}

function newLineKey(): string {
  return Math.random().toString(36).slice(2);
}


export function DocumentForm({ direction }: { direction: Direction }) {
  const isInflow = direction === "inflow";
  const can = usePermissions();
  const canSave = can(isInflow ? "inflow.create" : "outflow.create");
  const title = isInflow ? "Приходная накладная" : "Расходная накладная";
  const counterpartyLabel = isInflow ? "Поставщик" : "Контрагент";
  const titleBg = isInflow ? "var(--ink)" : "var(--brick)";

  const { user } = useAuth();
  const qc = useQueryClient();
  const storageKey = `kb.warehouse.form.${direction}.v1`;

  // Список единиц измерения — для select'а в ячейке "Ед."
  const unitsQ = useQuery({
    queryKey: ["warehouse-units"],
    queryFn: () => fetchUnits(),
    staleTime: 5 * 60 * 1000,
  });

  // Счётчики номеров — для placeholder «следующий №».
  const countersQ = useQuery({
    queryKey: ["warehouse-counters"],
    queryFn: () => fetchDocCounters(),
  });
  const nextDocNumber = countersQ.data
    ? String((isInflow ? countersQ.data.inflow : countersQ.data.outflow) + 1)
    : "";

  const units = unitsQ.data || [];

  // Load from localStorage
  const [header, setHeader] = useState<FormHeader>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.header) return { ...blankHeader(), ...parsed.header };
      }
    } catch {}
    const h = blankHeader();
    return h;
  });

  // Активные production_objects — для select'а «Объект» (только в форме отгрузки).
  // Это связка с production-модулем: после сохранения отгрузки warehouse_outflow_docs.object_id
  // указывает на конкретный проект, что разблокирует отчёт «расход материалов на объект».
  // ВАЖНО: блок ОБЯЗАН быть после `const [header, setHeader]` — иначе selectedObject
  // обращается к header в TDZ → runtime error «cannot access 'header' before initialization».
  const objectsQ = useQuery({
    queryKey: ["warehouse-objects"],
    queryFn: () => fetchObjects(),
    enabled: !isInflow,           // объекты нужны только для отгрузки
    staleTime: 60_000,
  });
  const objects: ObjectForSelect[] = objectsQ.data || [];
  const selectedObject = objects.find(o => o.id === header.objectId) || null;
  const [lines, setLines] = useState<FormLine[]>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed?.lines)) return parsed.lines;
      }
    } catch {}
    return [];
  });
  const [error, setError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState<string | null>(null);

  // Default employee = current user
  useEffect(() => {
    if (!header.employee && user?.display_name) {
      setHeader(h => ({ ...h, employee: user.display_name || "" }));
    }
  }, [user, header.employee]);

  // Persist to localStorage
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify({ header, lines })); } catch {}
  }, [header, lines, storageKey]);

  const totalSum = useMemo(() => {
    return lines.reduce((s, l) => {
      const price = isInflow ? l.price_buy : l.price_sell;
      return s + l.quantity * (price || 0);
    }, 0);
  }, [lines, isInflow]);

  function addItem(it: ItemRow) {
    // Если такой item_id уже в строках — увеличим кол-во на 1
    setLines(prev => {
      const idx = prev.findIndex(l => l.item_id === it.id);
      if (idx !== -1) {
        const upd = [...prev];
        upd[idx] = { ...upd[idx], quantity: upd[idx].quantity + 1 };
        return upd;
      }
      return [...prev, {
        key: newLineKey(),
        item_id: it.id, sku: it.sku, name: it.name, unit: it.unit,
        quantity: 1,
        price_buy: it.price_buy ?? 0,
        price_sell: it.price_sell ?? 0,
      }];
    });
  }

  function addEmptyLine() {
    setLines(prev => [...prev, {
      key: newLineKey(),
      item_id: null, sku: null, name: "", unit: null,
      quantity: 1, price_buy: 0, price_sell: 0,
    }]);
  }

  function updateLine(idx: number, patch: Partial<FormLine>) {
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));
  }

  function removeLine(idx: number) {
    setLines(prev => prev.filter((_, i) => i !== idx));
  }

  function clearForm() {
    if (!window.confirm("Очистить весь документ?")) return;
    setHeader(blankHeader());
    setLines([]);
    try { localStorage.removeItem(storageKey); } catch {}
    setError(null);
    setSaveOk(null);
  }

  // Сохранение через POST /api/warehouse/docs
  const saveMut = useMutation({
    mutationFn: async () => {
      if (!lines.length) throw new Error("Добавьте хотя бы одну позицию");
      const validLines = lines.filter(l => l.item_id !== null && l.quantity > 0);
      if (!validLines.length) throw new Error("Все строки пустые или без выбранной позиции");
      // Метаданные шапки храним в note (для расхода — больше полей)
      const noteParts: string[] = [];
      if (header.basis) noteParts.push(`Основание: ${header.basis}`);
      if (!isInflow) {
        if (header.address)     noteParts.push(`Адрес: ${header.address}`);
        if (header.phone)       noteParts.push(`Телефон: ${header.phone}`);
        if (header.shipDate)    noteParts.push(`Дата отгрузки: ${header.shipDate}`);
        if (header.requestType) noteParts.push(`Вид заявки: ${header.requestType}`);
        if (header.payment)     noteParts.push(`Оплата: ${header.payment}`);
      }
      return saveCartAsDoc({
        direction,
        warehouse: header.warehouse,
        doc_number: header.docNumber.trim() || null,
        doc_date: header.docDate,
        contractor_name: header.contractor.trim() || null,
        note: noteParts.join(" · ") || null,
        object_id: isInflow ? null : header.objectId,
        deal_amo_id: isInflow ? null : header.dealAmoId,
        items: validLines.map(l => ({
          item_id: l.item_id!,
          quantity: l.quantity,
          price: isInflow ? l.price_buy : l.price_sell,
        })),
      });
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ["warehouse-stocks"] });
      void qc.invalidateQueries({ queryKey: ["warehouse-overview"] });
      void qc.invalidateQueries({ queryKey: [`warehouse-${direction}-list`] });
      void qc.invalidateQueries({ queryKey: ["warehouse-counters"] });
      setSaveOk(`Сохранён документ № ${data.doc_number} (${data.items_count} поз.)`);
      setError(null);
      // Очищаем форму после успешного сохранения
      setLines([]);
      setHeader(h => ({
        ...h, docNumber: "", basis: "",
        contractor: !isInflow ? "" : h.contractor,
        objectId: !isInflow ? null : h.objectId,
        dealAmoId: !isInflow ? null : h.dealAmoId,
      }));
      setTimeout(() => setSaveOk(null), 4000);
    },
    onError: (e) => {
      const msg = e instanceof ApiError
        ? ((e.body as { detail?: string })?.detail || `HTTP ${e.status}`)
        : (e as Error).message;
      setError(msg);
    },
  });

  function field(label: string, input: React.ReactNode) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
        <div style={{ minWidth: 160, fontWeight: 700, fontSize: 13, color: "var(--ink-3)" }}>{label}</div>
        <div style={{ flex: 1 }}>{input}</div>
      </div>
    );
  }

  return (
    <div className="page">

      {/* Toolbar */}
      <div className="card" style={{ padding: 12, marginBottom: "var(--gap)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div style={{
            flex: 1, minWidth: 240,
            color: titleBg, fontSize: 22, fontWeight: 700,
          }}>
            {title}
          </div>
          <button type="button" className="btn btn-sm" disabled>Данные</button>
          <button type="button" className="btn btn-sm btn-primary" onClick={addEmptyLine}>
            <Icon name="plus" size={12} /> Добавить позицию
          </button>
          {canSave && (
            <button
              type="button" className="btn btn-sm btn-primary"
              onClick={() => saveMut.mutate()} disabled={saveMut.isPending || lines.length === 0}
            >
              <Icon name="save" size={12} /> {saveMut.isPending ? "Сохранение…" : "Сохранить"}
            </button>
          )}
          <button type="button" className="btn btn-sm btn-ghost" onClick={clearForm} title="Очистить">
            <Icon name="trash" size={14} />
          </button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => window.print()} title="Печать">
            <Icon name="file" size={14} />
          </button>
          <Link
            to={(direction === "inflow" ? "/inflows/history" : "/outflows/history") as "/"}
            className="btn btn-sm"
            style={{ textDecoration: "none" }}
          >
            <Icon name="clock" size={12} /> История
          </Link>
        </div>
      </div>

      {saveOk && <div className="alert alert-green" style={{ marginBottom: "var(--gap)" }}>
        <div className="alert-body">{saveOk}</div>
      </div>}
      {error && <div className="alert alert-red" style={{ marginBottom: "var(--gap)" }}>
        <div className="alert-body">{error}</div>
      </div>}

      {/* Шапка документа */}
      <div className="card" style={{ marginBottom: "var(--gap)", padding: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div>
            {field("Склад", (
              <select className="input-sm" value={header.warehouse}
                      onChange={e => setHeader(h => ({ ...h, warehouse: e.target.value as "СКЛАД 2_4" | "ИНСТРУМЕНТ 1.0" | "ОФИС" }))}
                      style={{ minWidth: 220 }}>
                <option value="СКЛАД 2_4">СКЛАД 2_4</option>
                <option value="ИНСТРУМЕНТ 1.0">ИНСТРУМЕНТ 1.0</option>
                <option value="ОФИС">ОФИС</option>
              </select>
            ))}
            {field(counterpartyLabel, (
              <input type="text" className="input-sm" style={{ width: "100%" }}
                     value={header.contractor}
                     onChange={e => setHeader(h => ({ ...h, contractor: e.target.value }))}
                     placeholder={isInflow ? 'ООО "МетСнаб"' : "ФИО клиента"} />
            ))}
            {field("Сотрудник", (
              <input type="text" className="input-sm" style={{ width: "100%" }}
                     value={header.employee}
                     onChange={e => setHeader(h => ({ ...h, employee: e.target.value }))}
                     placeholder="Шагин К.Н." />
            ))}
            {field("Основание (документ)", (
              <>
                <input
                  type="text"
                  className="input-sm"
                  style={{ width: "100%" }}
                  list="kb-doc-basis-types"
                  value={header.basis}
                  onChange={e => setHeader(h => ({ ...h, basis: e.target.value }))}
                  placeholder="счёт / счёт-фактура / накладная …"
                />
                {/* Справочник видов документов-оснований из оригинального Документы.xlsx */}
                <datalist id="kb-doc-basis-types">
                  <option value="счёт" />
                  <option value="счёт-фактура" />
                  <option value="товарная накладная" />
                  <option value="транспортная накладная" />
                </datalist>
              </>
            ))}
            {field(isInflow ? "Дата" : "Дата заказа", (
              <input type="date" className="input-sm" value={header.docDate}
                     onChange={e => setHeader(h => ({ ...h, docDate: e.target.value }))} />
            ))}
          </div>
          <div>
            {field("№ накладной", (
              <input type="text" className="input-sm" style={{ width: "100%" }}
                     value={header.docNumber}
                     onChange={e => setHeader(h => ({ ...h, docNumber: e.target.value }))}
                     placeholder={nextDocNumber ? `авто (следующий: ${nextDocNumber})` : "авто"} />
            ))}
            {!isInflow && <>
              {field("Объект (проект)", (
                <>
                  <select
                    className="input-sm" style={{ width: "100%" }}
                    value={header.objectId ?? ""}
                    onChange={(e) => {
                      const id = e.target.value === "" ? null : parseInt(e.target.value, 10);
                      const obj = id == null ? null : objects.find(o => o.id === id) || null;
                      setHeader(h => ({
                        ...h,
                        objectId: obj ? obj.id : null,
                        dealAmoId: obj?.deal_amo_id ?? null,
                        // Автоподтягиваем адрес объекта (можно перебить вручную)
                        address: obj?.address && !h.address ? obj.address : h.address,
                      }));
                    }}
                  >
                    <option value="">— Без привязки (продажа физ. лицу) —</option>
                    {objects.map(o => (
                      <option key={o.id} value={o.id}>
                        #{o.id} · {formatObjectLabel(o)}
                      </option>
                    ))}
                  </select>
                  {selectedObject && (
                    <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 4 }}>
                      Amo: {selectedObject.deal_amo_id ?? "—"}
                      {selectedObject.planned_end && ` · план готов: ${selectedObject.planned_end}`}
                    </div>
                  )}
                </>
              ))}
              {field("Адрес", (
                <input type="text" className="input-sm" style={{ width: "100%" }}
                       value={header.address}
                       onChange={e => setHeader(h => ({ ...h, address: e.target.value }))} />
              ))}
              {field("Телефон", (
                <input type="text" className="input-sm" style={{ width: "100%" }}
                       value={header.phone}
                       onChange={e => setHeader(h => ({ ...h, phone: e.target.value }))} />
              ))}
              {field("Дата отгрузки", (
                <input type="date" className="input-sm" value={header.shipDate}
                       onChange={e => setHeader(h => ({ ...h, shipDate: e.target.value }))} />
              ))}
              {field("Вид заявки", (
                <select className="input-sm" value={header.requestType}
                        onChange={e => setHeader(h => ({ ...h, requestType: e.target.value }))}>
                  <option value="Продажа">Продажа</option>
                  <option value="Доставка">Доставка</option>
                  <option value="Самовывоз">Самовывоз</option>
                  <option value="Внутреннее">Внутреннее</option>
                </select>
              ))}
              {field("Оплата, ₽", (
                <input type="text" inputMode="decimal" className="input-sm"
                       value={header.payment}
                       onChange={e => setHeader(h => ({ ...h, payment: e.target.value.replace(/[^\d.,]/g, "") }))}
                       placeholder="0,00" />
              ))}
            </>}
            <div style={{
              marginTop: 12, padding: "8px 12px", background: "var(--paper)",
              borderRadius: 4, fontSize: 14, fontWeight: 600, textAlign: "right",
            }}>
              Сумма: <span style={{ color: "var(--brick)" }}>{formatRub(totalSum)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Добавление позиции */}
      <div className="card" style={{ marginBottom: "var(--gap)", padding: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon name="search" size={14}/>
          <span style={{ fontSize: 12, color: "var(--mute)" }}>Найти позицию:</span>
          <div style={{ flex: 1, maxWidth: 600 }}>
            <ItemAutocomplete
              warehouse={header.warehouse}
              onSelect={addItem}
              placeholder="Поиск по артикулу или наименованию (мин. 2 символа)"
            />
          </div>
          <button type="button" className="btn btn-sm" onClick={addEmptyLine}>
            + пустая строка
          </button>
        </div>
      </div>

      {/* Таблица позиций */}
      <div className="card" style={{ padding: 0 }}>
        <div className="tbl-scroll">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th>Наименование</th>
                <th style={{ width: 110 }}>Артикул</th>
                <th style={{ width: 70 }}>Ед.</th>
                <th style={{ width: 90 }} className="num">Кол-во</th>
                {isInflow ? (
                  <>
                    <th style={{ width: 100 }} className="num">Цена закуп</th>
                    <th style={{ width: 100 }} className="num">Цена продажа</th>
                  </>
                ) : (
                  <th style={{ width: 100 }} className="num">Цена</th>
                )}
                <th style={{ width: 110 }} className="num">Сумма</th>
                <th style={{ width: 36 }}/>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr><td colSpan={isInflow ? 9 : 8} style={{ padding: 40, textAlign: "center", color: "var(--mute)" }}>
                  Добавьте первую позицию через поиск выше или кнопку «+ пустая строка»
                </td></tr>
              ) : lines.map((l, idx) => {
                const price = isInflow ? l.price_buy : l.price_sell;
                const sum = l.quantity * (price || 0);
                return (
                  <tr key={l.key}>
                    <td className="tabular" style={{ fontSize: 11, color: "var(--mute)" }}>{idx + 1}</td>
                    <td style={{ fontSize: 12 }}>
                      {l.item_id ? (
                        <span title={l.name}>{l.name}</span>
                      ) : (
                        <ItemAutocomplete
                          warehouse={header.warehouse}
                          onSelect={(it) => updateLine(idx, {
                            item_id: it.id, sku: it.sku, name: it.name, unit: it.unit,
                            price_buy: it.price_buy ?? l.price_buy,
                            price_sell: it.price_sell ?? l.price_sell,
                          })}
                          placeholder="Выбрать позицию…"
                        />
                      )}
                    </td>
                    {/* Артикул: всегда selectable через ItemAutocomplete (привязывает позицию из справочника). */}
                    <td style={{ fontSize: 12 }}>
                      <ItemAutocomplete
                        warehouse={header.warehouse}
                        initial={l.sku || ""}
                        placeholder="Артикул…"
                        onSelect={(it) => updateLine(idx, {
                          item_id: it.id, sku: it.sku, name: it.name, unit: it.unit,
                          price_buy: it.price_buy ?? l.price_buy,
                          price_sell: it.price_sell ?? l.price_sell,
                        })}
                      />
                    </td>
                    {/* Ед.: select из /api/warehouse/units (с fallback'ом на свободный ввод через datalist). */}
                    <td style={{ fontSize: 12 }}>
                      <input
                        type="text"
                        list={`units-${l.key}`}
                        className="input-sm"
                        value={l.unit || ""}
                        onChange={(e) => updateLine(idx, { unit: e.target.value || null })}
                        placeholder="—"
                        style={{ width: "100%", fontSize: 12 }}
                      />
                      <datalist id={`units-${l.key}`}>
                        {units.map(u => <option key={u} value={u} />)}
                      </datalist>
                    </td>
                    <td className="num">
                      <input
                        type="text" inputMode="decimal" className="input-sm"
                        value={String(l.quantity)}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value.replace(",", ".").replace(/[^\d.]/g, "")) || 0;
                          updateLine(idx, { quantity: v });
                        }}
                        style={{ width: "100%", textAlign: "right", fontSize: 12 }}
                      />
                    </td>
                    {isInflow ? (
                      <>
                        <td className="num">
                          <input
                            type="text" inputMode="decimal" className="input-sm"
                            value={String(l.price_buy)}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value.replace(",", ".").replace(/[^\d.]/g, "")) || 0;
                              updateLine(idx, { price_buy: v });
                            }}
                            style={{ width: "100%", textAlign: "right", fontSize: 12 }}
                          />
                        </td>
                        <td className="num">
                          <input
                            type="text" inputMode="decimal" className="input-sm"
                            value={String(l.price_sell)}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value.replace(",", ".").replace(/[^\d.]/g, "")) || 0;
                              updateLine(idx, { price_sell: v });
                            }}
                            style={{ width: "100%", textAlign: "right", fontSize: 12 }}
                          />
                        </td>
                      </>
                    ) : (
                      <td className="num">
                        <input
                          type="text" inputMode="decimal" className="input-sm"
                          value={String(l.price_sell)}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value.replace(",", ".").replace(/[^\d.]/g, "")) || 0;
                            updateLine(idx, { price_sell: v });
                          }}
                          style={{ width: "100%", textAlign: "right", fontSize: 12 }}
                        />
                      </td>
                    )}
                    <td className="num strong tabular" style={{ fontSize: 12 }}>{formatRub(sum)}</td>
                    <td>
                      <button
                        type="button" className="btn btn-sm" title="Удалить строку"
                        onClick={() => removeLine(idx)}
                        style={{ color: "var(--brick)" }}
                      >×</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {lines.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={isInflow ? 7 : 6} style={{ textAlign: "right", padding: "8px 12px", fontWeight: 600 }}>
                    Итого:
                  </td>
                  <td className="num strong tabular" style={{ padding: "8px 12px", color: "var(--brick)" }}>
                    {formatRub(totalSum)}
                  </td>
                  <td/>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}


